/**
 * PocketBase Client Service
 * Typed wrapper around PocketBase SDK with retry, timeout, dedup, and scoping logic
 */

import PocketBase from 'pocketbase'
import type { PBAuthRecord, PBListOptions, PBListResult, ClarityError } from '@/types'
import { ErrorCode as ErrorCodeEnum } from '@/types'
import { runtimeEnv } from './runtimeEnv'
import type { ErrorCode } from '@/types'
import { mapPocketBaseError } from '../../shared/pocketbaseErrors'

// Configuration constants
const REQUEST_TIMEOUT = 30000 // 30 seconds
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

type AuthChangeListener = () => void

/**
 * PocketBase Client Wrapper
 * Provides typed, resilient access to PocketBase backend
 */
class PBClient {
  private client: PocketBase
  private isInitialized = false
  private requestCache = new Map<string, { timestamp: number; data: unknown }>()
  private cacheTTL = 5000 // 5 second cache for reads
  private authListeners = new Set<AuthChangeListener>()
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null
  private readonly activityEvents: Array<keyof WindowEventMap> = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
  ]

  constructor() {
    const config = runtimeEnv.getPocketBaseConfig()
    this.client = new PocketBase(config.url)

    this.client.authStore.onChange((token, model) => {
      if (token && model) {
        this.resetInactivityTimer()
      } else {
        this.clearInactivityTimer()
      }

      this.notifyAuthChange()

      if (runtimeEnv.isDevelopment()) {
        console.log('[PB] Auth state changed:', { token: !!token, model: !!model })
      }
    })
  }

  /**
   * Initialize client – restore auth session from storage
   */
  async init(): Promise<void> {
    try {
      if (this.isInitialized) return

      this.setupActivityTracking()

      if (this.client.authStore.token) {
        try {
          await this.client.collection('users').authRefresh()
        } catch {
          this.handleAuthFailure('restore-failed')
        }
      }

      // Try to restore session from storage
      if (this.client.authStore.isValid) {
        this.resetInactivityTimer()
        if (runtimeEnv.isDevelopment()) {
          console.log('[PB] Session restored from storage')
        }
      }

      this.notifyAuthChange()
      this.isInitialized = true
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      throw new Error(`[PB Init] ${mapped.message}: ${mapped.code}`)
    }
  }

  /**
   * Get current authenticated user
   */
  getAuthUser(): PBAuthRecord | null {
    return this.client.authStore.model as PBAuthRecord | null
  }

  /**
   * Get auth token
   */
  getToken(): string {
    return this.client.authStore.token || ''
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.client.authStore.isValid && !!this.client.authStore.model
  }

  /**
   * Perform authentication with email + password
   */
  async authWithPassword(email: string, password: string): Promise<PBAuthRecord> {
    try {
      const response = await this.withRetry(() =>
        this.client.collection('users').authWithPassword(email, password)
      )
      this.resetInactivityTimer()
      this.notifyAuthChange()
      return response.record as PBAuthRecord
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      throw this.createError(mapped.code, mapped.message, mapped.statusCode)
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    this.client.authStore.clear()
    this.clearInactivityTimer()
    this.requestCache.clear()
    this.notifyAuthChange()
  }

  /**
   * Subscribe to auth state changes (login/logout/session expired)
   */
  subscribeAuthChange(listener: AuthChangeListener): () => void {
    this.authListeners.add(listener)
    return () => {
      this.authListeners.delete(listener)
    }
  }

  /**
   * GET: Fetch a single record by ID
   */
  async getRecord<T = Record<string, unknown>>(
    collection: string,
    recordId: string,
    options?: { expand?: string; fields?: string }
  ): Promise<T> {
    const cacheKey = `get:${collection}:${recordId}:${JSON.stringify(options || {})}`

    // Check cache first
    const cached = this.requestCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      return cached.data as T
    }

    try {
      const requestOptions: { expand?: string; fields?: string } = {};
      if (options?.expand) requestOptions.expand = options.expand;
      if (options?.fields) requestOptions.fields = options.fields;

      const record = await this.withRetry(() =>
        this.client.collection(collection).getOne(recordId, requestOptions)
      )

      this.requestCache.set(cacheKey, { timestamp: Date.now(), data: record })
      return record as T
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      if (mapped.code === ErrorCodeEnum.AUTH_ERROR) {
        this.handleAuthFailure('unauthorized')
      }
      throw this.createError(mapped.code, mapped.message, mapped.statusCode)
    }
  }

  /**
   * LIST: Fetch records with filtering and pagination
   */
  async listRecords<T = Record<string, unknown>>(
    collection: string,
    options?: PBListOptions & { expand?: string; fields?: string }
  ): Promise<PBListResult<T>> {
    const cacheKey = `list:${collection}:${JSON.stringify(options || {})}`

    // Check cache first
    const cached = this.requestCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      return cached.data as PBListResult<T>
    }

    try {
      const requestOptions: {
        sort?: string;
        filter?: string;
        expand?: string;
        fields?: string;
      } = {};
      if (options?.sort) requestOptions.sort = options.sort;
      if (options?.filter) requestOptions.filter = options.filter;
      if (options?.expand) requestOptions.expand = options.expand;
      if (options?.fields) requestOptions.fields = options.fields;

      const result = await this.withRetry(() =>
        this.client.collection(collection).getList<T>(options?.page || 1, options?.perPage || 50, requestOptions)
      )

      this.requestCache.set(cacheKey, { timestamp: Date.now(), data: result })
      return result as PBListResult<T>
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      if (mapped.code === ErrorCodeEnum.AUTH_ERROR) {
        this.handleAuthFailure('unauthorized')
      }
      throw this.createError(mapped.code, mapped.message, mapped.statusCode)
    }
  }

  /**
   * CREATE: Insert a new record
   */
  async createRecord<T = Record<string, unknown>>(collection: string, data: Record<string, unknown>): Promise<T> {
    try {
      this.clearListCache(collection)
      const record = await this.withRetry(() => this.client.collection(collection).create(data))
      return record as T
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      if (mapped.code === ErrorCodeEnum.AUTH_ERROR) {
        this.handleAuthFailure('unauthorized')
      }
      throw this.createError(mapped.code, mapped.message, mapped.statusCode)
    }
  }

  /**
   * UPDATE: Update an existing record
   */
  async updateRecord<T = Record<string, unknown>>(
    collection: string,
    recordId: string,
    data: Record<string, unknown>
  ): Promise<T> {
    try {
      this.clearListCache(collection)
      this.requestCache.delete(`get:${collection}:${recordId}`)
      const record = await this.withRetry(() => this.client.collection(collection).update(recordId, data))
      return record as T
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      if (mapped.code === ErrorCodeEnum.AUTH_ERROR) {
        this.handleAuthFailure('unauthorized')
      }
      throw this.createError(mapped.code, mapped.message, mapped.statusCode)
    }
  }

  /**
   * DELETE: Remove a record
   */
  async deleteRecord(collection: string, recordId: string): Promise<void> {
    try {
      this.clearListCache(collection)
      this.requestCache.delete(`get:${collection}:${recordId}`)
      await this.withRetry(() => this.client.collection(collection).delete(recordId))
    } catch (error) {
      const mapped = mapPocketBaseError(error)
      if (mapped.code === ErrorCodeEnum.AUTH_ERROR) {
        this.handleAuthFailure('unauthorized')
      }
      throw this.createError(mapped.code, mapped.message, mapped.statusCode)
    }
  }

  /**
   * Execute a function with automatic retry logic
   */
  private async withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: unknown

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        return await this.withTimeout(fn(), REQUEST_TIMEOUT)
      } catch (error) {
        lastError = error

        // Only retry on network errors, not on auth or validation errors
        const mapped = mapPocketBaseError(error)
        if (
          mapped.code === ErrorCodeEnum.AUTH_ERROR ||
          mapped.code === ErrorCodeEnum.VALIDATION_ERROR ||
          mapped.code === ErrorCodeEnum.NOT_FOUND ||
          mapped.code === ErrorCodeEnum.CONFLICT
        ) {
          throw error
        }

        if (attempt < MAX_RETRIES - 1) {
          await this.delay(RETRY_DELAY_MS * Math.pow(2, attempt))
        }
      }
    }

    throw lastError
  }

  /**
   * Wrap promise with timeout
   */
  private async withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Request timeout')), ms)),
    ])
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private notifyAuthChange(): void {
    this.authListeners.forEach((listener) => {
      try {
        listener()
      } catch {
        // Keep auth notifications resilient even with faulty listeners.
      }
    })
  }

  private setupActivityTracking(): void {
    if (typeof window === 'undefined') return

    const onActivity = () => {
      if (this.isAuthenticated()) {
        this.resetInactivityTimer()
      }
    }

    this.activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true })
    })

    window.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isAuthenticated()) {
        this.resetInactivityTimer()
      }
    })
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer()
    this.inactivityTimer = setTimeout(() => {
      this.handleAuthFailure('inactivity-timeout')
    }, INACTIVITY_TIMEOUT_MS)
  }

  private clearInactivityTimer(): void {
    if (!this.inactivityTimer) return
    clearTimeout(this.inactivityTimer)
    this.inactivityTimer = null
  }

  private handleAuthFailure(reason: string): void {
    const wasAuthed = !!this.client.authStore.token || this.client.authStore.isValid

    this.client.authStore.clear()
    this.clearInactivityTimer()
    this.requestCache.clear()
    this.notifyAuthChange()

    if (!wasAuthed || typeof window === 'undefined') return

    window.dispatchEvent(
      new CustomEvent('clarix:session-expired', {
        detail: { reason },
      })
    )
  }

  /**
   * Clear list cache for a collection
   */
  private clearListCache(collection: string): void {
    const keys = Array.from(this.requestCache.keys()).filter((k) => k.startsWith(`list:${collection}:`))
    keys.forEach((k) => this.requestCache.delete(k))
  }

  /**
   * Create typed error
   */
  private createError(code: ErrorCode, message: string, statusCode?: number): ClarityError {
    const error = new Error(message) as ClarityError
    error.code = code
    error.statusCode = statusCode
    return error
  }
}

// Export singleton instance
export const pbClient = new PBClient()

// Export type for reference
export type { PBAuthRecord, PBListOptions, PBListResult }
